import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { Modal } from '../../components/Modal';
import { PaginationControls } from '../../components/PaginationControls';
import { knowledgeBaseApiService } from '../../services/knowledgeBaseApi';
import { KnowledgeEntry } from '../../services/knowledgeBase';
import api from '../../config/api';

export const AdminKnowledgeBase: React.FC = () => {
  const [entries, setEntries] = useState<KnowledgeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<KnowledgeEntry | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('Toutes');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 10; // Pagination par 10 éléments
  
  const [formData, setFormData] = useState<Partial<KnowledgeEntry>>({
    question: '',
    answer: '',
    keywords: [],
    category: 'Pratique',
    arabic: '',
  });

  useEffect(() => {
    loadEntries();
  }, [currentPage, searchTerm, selectedCategory]);

  const loadEntries = async () => {
    try {
      setLoading(true);
      // Charger avec pagination côté API
      const params: any = {
        page: currentPage,
        page_size: itemsPerPage,
      };

      // Appliquer les filtres
      if (searchTerm) {
        params.search = searchTerm;
      }
      if (selectedCategory && selectedCategory !== 'Toutes') {
        params.category = selectedCategory;
      }

      const response = await api.get('/chat/knowledge-base/', { params });
      const data = response.data;

      // Gérer la réponse paginée
      if (data.results) {
        // Format DRF pagination
        setEntries(data.results.map((entry: any) => ({
          id: entry.id,
          question: entry.question,
          answer: entry.answer,
          category: entry.category,
          keywords: entry.keywords || [],
          arabic: entry.arabic || '',
          is_active: entry.is_active !== false,
        })));
        setTotalCount(data.count || 0);
      } else {
        // Format non paginé (fallback)
        const allEntries = Array.isArray(data) ? data : [];
        setEntries(allEntries);
        setTotalCount(allEntries.length);
      }
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      alert('Erreur lors du chargement de la base de connaissances');
      setEntries([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Cette fonction n'est plus utilisée, on utilise directement l'API

  const handleAdd = () => {
    setIsEditMode(false);
    setSelectedEntry(null);
    setFormData({
      question: '',
      answer: '',
      keywords: [],
      category: 'Pratique',
      arabic: '',
    });
    setIsModalOpen(true);
  };

  const handleEdit = (entry: KnowledgeEntry & { id?: number }) => {
    setIsEditMode(true);
    setSelectedEntry(entry);
    setFormData({
      question: entry.question,
      answer: entry.answer,
      keywords: [...entry.keywords],
      category: entry.category,
      arabic: entry.arabic || '',
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (entry: KnowledgeEntry & { id?: number }) => {
    if (!entry.id) {
      alert('Impossible de supprimer cette entrée (ID manquant)');
      return;
    }
    if (window.confirm(`Voulez-vous vraiment supprimer cette entrée : "${entry.question}" ?`)) {
      try {
        await api.delete(`/chat/knowledge-base/${entry.id}/`);
        await loadEntries(); // Recharger la liste
        alert('Entrée supprimée avec succès');
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        alert('Erreur lors de la suppression');
      }
    }
  };

  const handleSave = async () => {
    if (!formData.question || !formData.answer) {
      alert('Veuillez remplir au moins la question et la réponse.');
      return;
    }

    const keywords = formData.keywords || [];
    if (keywords.length === 0) {
      // Générer des mots-clés automatiques à partir de la question
      const autoKeywords = formData.question
        .toLowerCase()
        .split(' ')
        .filter((word: string) => word.length > 3)
        .slice(0, 5);
      keywords.push(...autoKeywords);
    }

    const payload = {
      question: formData.question,
      answer: formData.answer,
      keywords: keywords,
      category: formData.category || 'Pratique',
      arabic: formData.arabic || undefined,
    };

    try {
      if (isEditMode && selectedEntry && (selectedEntry as any).id) {
        // Mise à jour
        await api.put(`/chat/knowledge-base/${(selectedEntry as any).id}/`, payload);
        alert('Entrée mise à jour avec succès');
      } else {
        // Création
        await api.post('/chat/knowledge-base/', payload);
        alert('Entrée créée avec succès');
      }
      knowledgeBaseApiService.invalidateCache(); // Invalider le cache
      await loadEntries(); // Recharger la liste
      setIsModalOpen(false);
      resetForm();
    } catch (error: any) {
      console.error('Erreur lors de la sauvegarde:', error);
      alert(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const resetForm = () => {
    setFormData({
      question: '',
      answer: '',
      keywords: [],
      category: 'Pratique',
      arabic: '',
    });
    setSelectedEntry(null);
    setIsEditMode(false);
  };

  const handleKeywordChange = (value: string) => {
    const keywords = value.split(',').map(k => k.trim()).filter(k => k.length > 0);
    setFormData({ ...formData, keywords });
  };

  const [categories, setCategories] = useState<string[]>(['Toutes', 'Pratique', 'Croyance', 'Histoire', 'Jurisprudence', 'Éthique', 'Autre']);
  
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const cats = await knowledgeBaseApiService.getCategories();
        setCategories(['Toutes', ...cats]);
      } catch (error) {
        console.error('Erreur lors du chargement des catégories:', error);
      }
    };
    loadCategories();
  }, []);
  // Le filtrage est maintenant fait côté API, mais on garde un filtrage local minimal pour les mots-clés
  const filteredEntries = entries;
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  // Réinitialiser la page quand on change les filtres (mais pas si on vient déjà de la page 1)
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, selectedCategory]);

  if (loading) {
    return (
      <AdminLayout activeSection="/admin/knowledge-base">
        <div className="flex justify-center items-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-green-700 border-t-transparent mx-auto mb-4"></div>
            <p className="text-gray-600 font-medium">Chargement...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activeSection="/admin/knowledge-base">
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-3xl font-bold text-green-700 mb-2">Base de Connaissances</h2>
              <p className="text-gray-600">Gérez les FAQ du chat islamique</p>
            </div>
            <button
              onClick={handleAdd}
              className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white px-6 py-3 rounded-xl font-bold transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center space-x-2"
            >
              <span>➕</span>
              <span>Ajouter une entrée</span>
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl p-4">
              <div className="text-2xl font-bold">{entries.length}</div>
              <div className="text-sm opacity-90">Total entrées</div>
            </div>
            <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-xl p-4">
              <div className="text-2xl font-bold">{categories.length}</div>
              <div className="text-sm opacity-90">Catégories</div>
            </div>
            <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-xl p-4">
              <div className="text-2xl font-bold">{totalCount}</div>
              <div className="text-sm opacity-90">Total entrées</div>
            </div>
            <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white rounded-xl p-4">
              <div className="text-2xl font-bold">
                {entries.reduce((acc, e) => acc + e.keywords.length, 0)}
              </div>
              <div className="text-sm opacity-90">Mots-clés total</div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-xl p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                🔍 Rechercher
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Rechercher par question, réponse ou mot-clé..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                📂 Catégorie
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="Toutes">Toutes les catégories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Entries List */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="divide-y divide-gray-200">
            {filteredEntries.length === 0 ? (
              <div className="p-12 text-center">
                <div className="text-6xl mb-4">📚</div>
                <p className="text-gray-600 text-lg">Aucune entrée trouvée</p>
                <button
                  onClick={handleAdd}
                  className="mt-4 bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors"
                >
                  Ajouter la première entrée
                </button>
              </div>
            ) : (
              filteredEntries.map((entry) => (
                <div key={(entry as any).id || entry.question} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-semibold">
                          {entry.category}
                        </span>
                        {entry.arabic && (
                          <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-semibold" dir="rtl">
                            {entry.arabic}
                          </span>
                        )}
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{entry.question}</h3>
                      <p className="text-gray-600 mb-3 line-clamp-2">{entry.answer.substring(0, 200)}...</p>
                      <div className="flex flex-wrap gap-2">
                        {entry.keywords.map((keyword: string, idx: number) => (
                          <span
                            key={idx}
                            className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs"
                          >
                            {keyword}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex space-x-2 ml-4">
                      <button
                        onClick={() => handleEdit(entry)}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
                      >
                        ✏️ Modifier
                      </button>
                      <button
                        onClick={() => handleDelete(entry)}
                        className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-colors"
                      >
                        🗑️ Supprimer
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="p-4 border-t border-gray-200">
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                loading={loading}
                onPageChange={(page) => {
                  setCurrentPage(page);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          resetForm();
        }}
        title={isEditMode ? 'Modifier l\'entrée' : 'Ajouter une entrée'}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Question *
            </label>
            <input
              type="text"
              value={formData.question}
              onChange={(e) => setFormData({ ...formData, question: e.target.value })}
              placeholder="Ex: Comment faire la prière ?"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Réponse *
            </label>
            <textarea
              value={formData.answer}
              onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
              placeholder="Réponse complète avec formatage markdown..."
              rows={8}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Mots-clés (séparés par des virgules)
            </label>
            <input
              type="text"
              value={formData.keywords?.join(', ') || ''}
              onChange={(e) => handleKeywordChange(e.target.value)}
              placeholder="prière, salat, comment prier..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              Les mots-clés permettent de trouver cette entrée lors de la recherche
            </p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Catégorie
            </label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="Pratique">Pratique</option>
              <option value="Fondamentaux">Fondamentaux</option>
              <option value="Histoire">Histoire</option>
              <option value="Jurisprudence">Jurisprudence</option>
              <option value="Autre">Autre</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Terme en arabe (optionnel)
            </label>
            <input
              type="text"
              value={formData.arabic || ''}
              onChange={(e) => setFormData({ ...formData, arabic: e.target.value })}
              placeholder="Ex: الصلاة"
              dir="rtl"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              onClick={() => {
                setIsModalOpen(false);
                resetForm();
              }}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handleSave}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              {isEditMode ? 'Modifier' : 'Ajouter'}
            </button>
          </div>
        </div>
      </Modal>
    </AdminLayout>
  );
};

